/**
 * Groups similar keywords using optimized frontend text similarity algorithms
 * with more aggressive grouping to create fewer, larger groups
 */
export function groupSimilarKeywords(keywords, threshold = 0.6) { // Lower default threshold
  if (!keywords || keywords.length === 0) return [];
  
  const groups = [];
  const assigned = new Set();
  
  // First, deduplicate keywords by consolidating metrics for identical keyword text
  const keywordMap = new Map();
  
  keywords.forEach(kw => {
    const keywordText = kw.keyword.toLowerCase().trim();
    if (!keywordMap.has(keywordText)) {
      keywordMap.set(keywordText, {
        keyword: kw.keyword, // Keep original casing from first occurrence
        stats: {
          clicks1: 0,
          clicks2: 0,
          impr1: 0,
          impr2: 0,
          position1: 0,
          position2: 0,
          ctr1: 0,
          ctr2: 0,
          positionChange: 0
        },
        pages: [], // Track all pages this keyword appears on
        intent: kw.intent || null,
        totalImpressions: 0 // For weighted averages
      });
    }
    
    const consolidated = keywordMap.get(keywordText);
    
    // Add this page to the list
    consolidated.pages.push({
      url: kw.pageUrl,
      clicks: kw.stats?.clicks2 || 0,
      impressions: kw.stats?.impr2 || 0,
      position: kw.stats?.position2 || 0
    });
    
    // Accumulate metrics (totals for clicks/impressions, weighted averages for positions/CTR)
    consolidated.stats.clicks1 += kw.stats?.clicks1 || 0;
    consolidated.stats.clicks2 += kw.stats?.clicks2 || 0;
    consolidated.stats.impr1 += kw.stats?.impr1 || 0;
    consolidated.stats.impr2 += kw.stats?.impr2 || 0;
    consolidated.totalImpressions += (kw.stats?.impr2 || 0);
    
    // For position and CTR, we'll calculate weighted averages after consolidation
  });
  
  // Calculate weighted averages for position and CTR
  const deduplicatedKeywords = Array.from(keywordMap.values()).map(kw => {
    if (kw.totalImpressions > 0) {
      // Calculate impression-weighted average position
      let weightedPosition = 0;
      let totalWeight = 0;
      
      kw.pages.forEach(page => {
        if (page.impressions > 0 && page.position > 0) {
          weightedPosition += page.position * page.impressions;
          totalWeight += page.impressions;
        }
      });
      
      kw.stats.position2 = totalWeight > 0 ? weightedPosition / totalWeight : 0;
      kw.stats.ctr2 = kw.stats.impr2 > 0 ? (kw.stats.clicks2 / kw.stats.impr2) * 100 : 0;
    }
    
    // Set the top performing page as the primary pageUrl for the consolidated keyword
    kw.pageUrl = kw.pages.length > 0 
      ? kw.pages.sort((a, b) => b.clicks - a.clicks)[0].url 
      : '';
    
    return kw;
  });
  
  console.log(`📊 Keyword Deduplication: ${keywords.length} → ${deduplicatedKeywords.length} keywords (${keywords.length - deduplicatedKeywords.length} duplicates removed)`);
  
  // Sort by clicks (descending) to prioritize important keywords as group representatives
  const sortedKeywords = deduplicatedKeywords.sort((a, b) => {
    const clicksA = (a.stats?.clicks2 || a.c2 || a.clicks2 || 0);
    const clicksB = (b.stats?.clicks2 || b.c2 || b.clicks2 || 0);
    return clicksB - clicksA;
  });
  
  // Pre-process keywords for faster comparison
  const processedKeywords = sortedKeywords.map(k => ({
    original: k,
    processed: normalizeKeyword(k.keyword),
    words: extractSignificantWords(k.keyword),
    length: k.keyword.length,
    stems: stemWords(k.keyword) // Add word stemming for better matching
  }));
  
  // First pass: identify potential clusters by common words AND stems
  const wordToKeywords = {};
  const stemToKeywords = {};
  
  processedKeywords.forEach(keyword => {
    // Index by words
    keyword.words.forEach(word => {
      if (word.length > 3) {
        if (!wordToKeywords[word]) wordToKeywords[word] = [];
        wordToKeywords[word].push(keyword);
      }
    });
    
    // Index by stems for better semantic matching
    keyword.stems.forEach(stem => {
      if (stem.length > 3) {
        if (!stemToKeywords[stem]) stemToKeywords[stem] = [];
        stemToKeywords[stem].push(keyword);
      }
    });
  });
  
  // Create groups with optimizations and more aggressive clustering
  for (let i = 0; i < processedKeywords.length; i++) {
    const keyword = processedKeywords[i];
    if (assigned.has(keyword.processed)) continue;
    
    const group = {
      representative: keyword.original,
      members: [keyword.original],
      totalClicks: Number(keyword.original.stats?.clicks2 || keyword.original.c2 || keyword.original.clicks2 || 0),
      totalImpressions: Number(keyword.original.stats?.impr2 || keyword.original.i2 || keyword.original.impr2 || 0),
      coreWords: new Set([...keyword.words, ...keyword.stems]) // Track core concepts
    };
    
    // Find potential candidates that share words OR stems with this keyword
    const candidateSet = new Set();
    
    // Check word matches
    keyword.words.forEach(word => {
      if (word.length > 3 && wordToKeywords[word]) {
        wordToKeywords[word].forEach(candidate => {
          if (candidate.processed !== keyword.processed && !assigned.has(candidate.processed)) {
            candidateSet.add(candidate);
          }
        });
      }
    });
    
    // Check stem matches (broader semantic matching)
    keyword.stems.forEach(stem => {
      if (stem.length > 3 && stemToKeywords[stem]) {
        stemToKeywords[stem].forEach(candidate => {
          if (candidate.processed !== keyword.processed && !assigned.has(candidate.processed)) {
            candidateSet.add(candidate);
          }
        });
      }
    });
    
    // Convert to array for processing
    const candidates = Array.from(candidateSet);
    
    // Find similar keywords with optimized comparison
    for (const candidate of candidates) {
      // 1. Check for phrase containment (one keyword contains the other)
      if (keyword.processed.includes(candidate.processed) || 
          candidate.processed.includes(keyword.processed)) {
        addToGroup(group, candidate);
        assigned.add(candidate.processed);
        continue;
      }
      
      // 2. Check for high word + stem overlap (improved semantic matching)
      const wordOverlap = [...keyword.words].filter(word => candidate.words.has(word)).length;
      const stemOverlap = [...keyword.stems].filter(stem => candidate.stems.has(stem)).length;
      
      const totalKeywordConcepts = Math.max(keyword.words.size, keyword.stems.size);
      const totalCandidateConcepts = Math.max(candidate.words.size, candidate.stems.size);
      const conceptOverlapRatio = (wordOverlap + stemOverlap) / Math.min(totalKeywordConcepts, totalCandidateConcepts);
      
      if (conceptOverlapRatio >= 0.5) { // More intelligent concept overlap
        addToGroup(group, candidate);
        assigned.add(candidate.processed);
        continue;
      }
      
      // 3. Check for semantic patterns (improved pattern matching)
      if (hasSemanticSimilarity(keyword, candidate)) {
        addToGroup(group, candidate);
        assigned.add(candidate.processed);
        continue;
      }
      
      // 4. For remaining candidates, use full similarity calculation but with lower threshold
      const similarity = calculateSimilarity(keyword.processed, candidate.processed);
      if (similarity >= threshold) {
        addToGroup(group, candidate);
        assigned.add(candidate.processed);
      }
    }
    
    // Only create a group if it has multiple members or is a high-traffic keyword
    if (group.members.length > 1 || group.totalClicks > 10) {
      assigned.add(keyword.processed);
      
      // Generate intelligent topic name
      group.topic = generateTopicName(group);
      groups.push(group);
    }
  }
  
  // Second pass: assign any remaining keywords to their closest group
  const remainingKeywords = processedKeywords.filter(k => !assigned.has(k.processed));
  
  for (const keyword of remainingKeywords) {
    let bestGroup = null;
    let bestSimilarity = 0;
    
    for (const group of groups) {
      const repKeyword = group.representative.keyword.toLowerCase();
      const similarity = calculateSimilarity(keyword.processed, repKeyword);
      
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestGroup = group;
      }
    }
    
    // If we found a reasonably similar group, add to it
    if (bestSimilarity >= 0.4) { // Very low threshold for second pass
      addToGroup(bestGroup, keyword);
    } else {
      // Otherwise create a singleton group
      const singletonGroup = {
        representative: keyword.original,
        members: [keyword.original],
        totalClicks: Number(keyword.original.stats?.clicks2 || keyword.original.c2 || keyword.original.clicks2 || 0),
        totalImpressions: Number(keyword.original.stats?.impr2 || keyword.original.i2 || keyword.original.impr2 || 0),
        coreWords: new Set([...keyword.words, ...keyword.stems])
      };
      singletonGroup.topic = generateTopicName(singletonGroup);
      groups.push(singletonGroup);
    }
  }
  
  // Sort groups by total clicks and limit to reasonable number of groups
  const sortedGroups = groups.sort((a, b) => b.totalClicks - a.totalClicks);
  
  // Merge very small groups with similar groups to reduce noise
  return mergeSmallGroups(sortedGroups);
}

/**
 * Helper function to add candidate to group and update metrics
 */
function addToGroup(group, candidate) {
  group.members.push(candidate.original);
  group.totalClicks += Number(candidate.original.stats?.clicks2 || candidate.original.c2 || candidate.original.clicks2 || 0);
  group.totalImpressions += Number(candidate.original.stats?.impr2 || candidate.original.i2 || candidate.original.impr2 || 0);
  
  // Update core concepts
  candidate.words.forEach(word => group.coreWords.add(word));
  candidate.stems.forEach(stem => group.coreWords.add(stem));
}

/**
 * Normalize keyword for better comparison
 */
function normalizeKeyword(keyword) {
  return keyword.toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

/**
 * Extract significant words (filter stop words and short words)
 */
function extractSignificantWords(keyword) {
  const stopWords = new Set(['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'how', 'what', 'when', 'where', 'why', 'who']);
  
  return new Set(
    normalizeKeyword(keyword)
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.has(word))
  );
}

/**
 * Simple word stemming for better semantic matching
 */
function stemWords(keyword) {
  const words = extractSignificantWords(keyword);
  const stems = new Set();
  
  words.forEach(word => {
    // Simple stemming rules
    let stem = word;
    
    // Remove common suffixes
    if (word.endsWith('ing')) stem = word.slice(0, -3);
    else if (word.endsWith('ed')) stem = word.slice(0, -2);
    else if (word.endsWith('er')) stem = word.slice(0, -2);
    else if (word.endsWith('est')) stem = word.slice(0, -3);
    else if (word.endsWith('ly')) stem = word.slice(0, -2);
    else if (word.endsWith('tion')) stem = word.slice(0, -4);
    else if (word.endsWith('ness')) stem = word.slice(0, -4);
    else if (word.endsWith('ment')) stem = word.slice(0, -4);
    else if (word.endsWith('able')) stem = word.slice(0, -4);
    else if (word.endsWith('ible')) stem = word.slice(0, -4);
    else if (word.endsWith('ful')) stem = word.slice(0, -3);
    else if (word.endsWith('less')) stem = word.slice(0, -4);
    else if (word.endsWith('ous')) stem = word.slice(0, -3);
    else if (word.endsWith('ive')) stem = word.slice(0, -3);
    else if (word.endsWith('ize')) stem = word.slice(0, -3);
    else if (word.endsWith('ise')) stem = word.slice(0, -3);
    else if (word.endsWith('s') && word.length > 3) stem = word.slice(0, -1); // Simple plural
    
    if (stem.length > 2) {
      stems.add(stem);
    }
  });
  
  return stems;
}

/**
 * Check for semantic similarity patterns
 */
function hasSemanticSimilarity(keyword1, keyword2) {
  // Check for common semantic patterns
  
  // Brand + product pattern (e.g., "nike shoes" vs "adidas shoes")
  const getPattern = (words) => {
    const productWords = ['shoes', 'shirt', 'pants', 'jacket', 'bag', 'phone', 'laptop', 'car', 'bike'];
    const actionWords = ['buy', 'review', 'compare', 'best', 'cheap', 'price'];
    
    const hasProduct = [...words].some(word => productWords.includes(word));
    const hasAction = [...words].some(word => actionWords.includes(word));
    
    return { hasProduct, hasAction };
  };
  
  const pattern1 = getPattern(keyword1.words);
  const pattern2 = getPattern(keyword2.words);
  
  // If both have the same pattern type (product or action), they're more likely related
  if ((pattern1.hasProduct && pattern2.hasProduct) || (pattern1.hasAction && pattern2.hasAction)) {
    return true;
  }
  
  return false;
}

/**
 * Generate intelligent topic names based on group content
 */
function generateTopicName(group) {
  if (group.members.length === 1) {
    return group.representative.keyword;
  }
  
  // Find most common significant words across all group members
  const wordCount = {};
  
  group.members.forEach(member => {
    const words = extractSignificantWords(member.keyword);
    words.forEach(word => {
      wordCount[word] = (wordCount[word] || 0) + 1;
    });
  });
  
  // Get words that appear in at least 30% of group members
  const threshold = Math.max(1, Math.floor(group.members.length * 0.3));
  const commonWords = Object.entries(wordCount)
    .filter(([word, count]) => count >= threshold)
    .sort((a, b) => b[1] - a[1])
    .map(([word]) => word)
    .slice(0, 3); // Take top 3 common words
  
  if (commonWords.length > 0) {
    return commonWords.join(' ');
  }
  
  // Fallback to representative keyword
  return group.representative.keyword;
}

/**
 * Merge very small groups with similar larger groups to reduce noise
 */
function mergeSmallGroups(groups) {
  const minGroupSize = 2;
  const largeGroups = groups.filter(g => g.members.length >= minGroupSize);
  const smallGroups = groups.filter(g => g.members.length < minGroupSize);
  
  // Try to merge small groups with large groups
  smallGroups.forEach(smallGroup => {
    let bestMatch = null;
    let bestSimilarity = 0;
    
    largeGroups.forEach(largeGroup => {
      // Calculate concept overlap
      const overlap = [...smallGroup.coreWords].filter(concept => largeGroup.coreWords.has(concept)).length;
      const similarity = overlap / Math.min(smallGroup.coreWords.size, largeGroup.coreWords.size);
      
      if (similarity > bestSimilarity && similarity >= 0.3) {
        bestSimilarity = similarity;
        bestMatch = largeGroup;
      }
    });
    
    if (bestMatch) {
      // Merge into the best matching large group
      bestMatch.members.push(...smallGroup.members);
      bestMatch.totalClicks += smallGroup.totalClicks;
      bestMatch.totalImpressions += smallGroup.totalImpressions;
      smallGroup.coreWords.forEach(concept => bestMatch.coreWords.add(concept));
      
      // Regenerate topic name for the merged group
      bestMatch.topic = generateTopicName(bestMatch);
    } else {
      // Keep as separate group if no good match found
      largeGroups.push(smallGroup);
    }
  });
  
  return largeGroups.sort((a, b) => b.totalClicks - a.totalClicks);
}

/**
 * Optimized similarity calculation with more aggressive matching
 */
function calculateSimilarity(a, b) {
  // Exact match
  if (a === b) return 1;
  
  // Check for one being a substring of the other
  if (a.includes(b) || b.includes(a)) {
    const lengthRatio = Math.min(a.length, b.length) / Math.max(a.length, b.length);
    // Return higher similarity for closer length matches
    return 0.7 + (lengthRatio * 0.3);
  }
  
  // Word overlap score
  const wordsA = a.split(/\s+/).filter(w => w.length > 2);
  const wordsB = b.split(/\s+/).filter(w => w.length > 2);
  
  // If either has no significant words, use character-based comparison
  if (wordsA.length === 0 || wordsB.length === 0) {
    return approximateEditSimilarity(a, b);
  }
  
  const uniqueWordsA = new Set(wordsA);
  const uniqueWordsB = new Set(wordsB);
  
  // Calculate Jaccard similarity for words
  const intersection = new Set([...uniqueWordsA].filter(x => uniqueWordsB.has(x)));
  const union = new Set([...uniqueWordsA, ...uniqueWordsB]);
  
  // More aggressive word similarity - give more weight to matches
  const wordSimilarity = intersection.size / Math.min(uniqueWordsA.size, uniqueWordsB.size);
  
  // If word similarity is decent, boost it
  if (wordSimilarity >= 0.5) {
    return wordSimilarity * 1.2; // Boost similarity for good word matches
  }
  
  // For lower word similarity, include edit distance
  const editSimilarity = approximateEditSimilarity(a, b);
  
  // Combine scores with weights favoring word similarity
  return (wordSimilarity * 0.7) + (editSimilarity * 0.3);
}

/**
 * Faster approximate edit similarity
 */
function approximateEditSimilarity(a, b) {
  // For very different length strings, return low similarity
  if (Math.abs(a.length - b.length) / Math.max(a.length, b.length) > 0.5) {
    return 0.2;
  }
  
  // Compare character n-grams (more forgiving than exact matching)
  const getNGrams = (str, n) => {
    const grams = [];
    for (let i = 0; i <= str.length - n; i++) {
      grams.push(str.substring(i, i + n));
    }
    return new Set(grams);
  };
  
  // Use character trigrams
  const aGrams = getNGrams(a, 3);
  const bGrams = getNGrams(b, 3);
  
  // Calculate n-gram similarity
  const intersection = new Set([...aGrams].filter(x => bGrams.has(x)));
  const union = new Set([...aGrams, ...bGrams]);
  
  return intersection.size / union.size;
}